/**
 * Settings screen — plain DOM, mounted from the main menu OR the pause
 * overlay. (§7.1 camera live preview, §6.8 quality, §4.1 rebindable controls)
 *
 * Same discipline as hud.ts: no React, one style element, everything cleaned
 * up by dispose(). The screen edits the GameSettings object it was handed IN
 * PLACE and fires `onChange` after every edit — the caller decides persistence
 * (main.ts saves to localStorage on every change; cheap, and a crash never
 * loses a rebind).
 *
 * Live-preview seams (§7.1 "user-adjustable with live preview"):
 *   - camera height/distance/FOV write TUNABLES.camera via applyCameraSettings;
 *     render/camera.ts reads that object every frame, so in a running match the
 *     slider IS the preview. From the menu the values simply persist.
 *   - camera mode + quality tier + control map apply through the optional
 *     `live` hooks when a match is running.
 *
 * Rebinding: click a key chip → "press any key" capture (Escape cancels).
 * Assigning a key that already drives a different action is REFUSED and the
 * conflicting row is flashed (controlMap.ts `conflictForKey`) — a silent steal
 * would leave some other action dead without the player noticing.
 */

import {
  ACTION_LABELS,
  REBINDABLE_ACTIONS,
  conflictForKey,
  defaultControlMap,
  keyLabel,
  keysForAction,
  rebindKey,
  unbindKey,
  type BindableAction,
  type ControlMap,
} from '../input/controlMap.js';
import { CameraMode, cameraModeName } from '../render/camera.js';
import type { QualityTier } from '../render/context.js';
import {
  AIM_SENSITIVITY_RANGE,
  BALL_FOCUS_CHOICES,
  BALL_FOCUS_LABELS,
  CAMERA_DISTANCE_RANGE,
  CAMERA_FOV_RANGE,
  CAMERA_HEIGHT_RANGE,
  CAMERA_SMOOTHING_RANGE,
  DIFFICULTY_CHOICES,
  HALF_MINUTES_CHOICES,
  MOUSE_SENSITIVITY_RANGE,
  QUALITY_CHOICES,
  applyCameraSettings,
  type GameSettings,
  type QualityChoice,
} from './settings.js';
import { CROSSHAIR_SIZE_CHOICES, CROSSHAIR_SIZE_PRESETS } from './crosshair-geometry.js';

/** Hooks into a RUNNING match, so edits apply live. All optional. */
export interface LiveHooks {
  camera?: { setMode(mode: number): void };
  renderer?: { setQuality(tier: QualityTier): void };
  input?: { setMap(map: ControlMap): void; setSensitivity(mouse: number, aim: number): void };
  /** game/audio.ts's createAudioSystem() — live volume preview (§11), same
   *  pattern as the camera/quality/input hooks above. */
  audio?: { setMasterVolume(v: number): void };
  /** ui/menuMusic.ts's mountMenuMusic() — only ever passed when Settings is
   *  opened FROM the arena menu (main.ts's onOpenSettings), since that is the
   *  only context menu music is playing in at all. Both the Master AND Music
   *  sliders call this — the track's gain is musicVolume * masterVolume, so
   *  either one moving needs to reach it. */
  music?: { setVolume(musicVolume: number, masterVolume: number): void };
}

export interface SettingsScreenOptions {
  settings: GameSettings;
  onChange: (s: GameSettings) => void;
  onClose: () => void;
  live?: LiveHooks;
}

export interface SettingsScreen {
  dispose(): void;
}

const MODES: readonly CameraMode[] = [
  // TacticalFollow leads because it is the DEFAULT (ui/settings.ts
  // DEFAULT_CAMERA_MODE). It was previously missing from this list entirely —
  // reachable only through the C camera-cycle — which would have left the
  // dropdown unable to display, or return to, the mode the game boots in.
  CameraMode.TacticalFollow,
  CameraMode.Broadcast,
  CameraMode.Tele,
  CameraMode.ProCam,
  CameraMode.PlayerCam,
  CameraMode.Dynamic,
  // CameraMode.Free is deliberately NOT offered: nothing in a match drives
  // setFreePose, so selecting it parked the camera at a fixed point staring at
  // the ball. It stays reserved for a future spectator/replay driver.
];

export function mountSettingsScreen(
  container: HTMLElement,
  opts: SettingsScreenOptions,
): SettingsScreen {
  const s = opts.settings;
  const root = document.createElement('div');
  root.className = 'fgset';
  root.innerHTML = `
    <div class="fgset-panel">
      <div class="fgset-head">
        <h2>SETTINGS</h2>
        <button class="fgset-close" data-fg="close">✕ CLOSE</button>
      </div>
      <div class="fgset-body">

        <section>
          <h3>CAMERA <span class="fgset-hint">changes preview live in a match (§7.1)</span></h3>
          <!--
            Owner: "show the cam setting visually in a box how it will look".
            A SCHEMATIC, not a live 3D render — a second renderer inside a
            settings modal is a real subsystem (its own scene, its own frame
            loop, its own dispose path) for a box whose entire job is "is the
            camera high and far, or low and close", which two axes and an
            angle already answer. Side-view (height up the Y axis, distance
            along X): a camera marker at the slider-driven position, a wedge
            showing the FOV cone converging on a fixed player marker. Height
            and distance are normalised to THEIR OWN range independently (see
            updateCamPreview below) rather than drawn to one shared metric
            scale — camera height runs 4-45m and distance 5-60m, wildly
            different magnitudes a real player (under 2m) would vanish inside,
            so this reads as "where the slider sits in its own range", the
            thing a player is actually adjusting, not a scale drawing.
          -->
          <div class="fgset-campreview">
            <svg viewBox="0 0 280 150" data-fg="camPreviewSvg" aria-hidden="true">
              <line class="fgcp-ground" x1="14" y1="128" x2="266" y2="128" />
              <polygon class="fgcp-fov" data-fg="camPreviewFov" points="0,0 0,0 0,0" />
              <line class="fgcp-sight" data-fg="camPreviewSight" x1="0" y1="0" x2="0" y2="0" />
              <circle class="fgcp-player" cx="230" cy="128" r="5" />
              <line class="fgcp-playerpin" x1="230" y1="123" x2="230" y2="108" />
              <g data-fg="camPreviewCam" class="fgcp-cam">
                <circle r="6" />
                <circle r="2.4" class="fgcp-camlens" />
              </g>
            </svg>
            <p class="fgset-hint">the wedge is field of view, not what is actually on screen</p>
          </div>
          <label class="fgset-row">
            <span>Mode</span>
            <select data-fg="camMode"></select>
          </label>
          <label class="fgset-row">
            <span>Height <em data-fg="heightVal"></em></span>
            <input type="range" data-fg="height" min="${CAMERA_HEIGHT_RANGE.min}" max="${CAMERA_HEIGHT_RANGE.max}" step="0.5">
          </label>
          <label class="fgset-row">
            <span>Distance <em data-fg="distanceVal"></em></span>
            <input type="range" data-fg="distance" min="${CAMERA_DISTANCE_RANGE.min}" max="${CAMERA_DISTANCE_RANGE.max}" step="0.5">
          </label>
          <label class="fgset-row">
            <span>Field of view <em data-fg="fovVal"></em></span>
            <input type="range" data-fg="fov" min="${CAMERA_FOV_RANGE.min}" max="${CAMERA_FOV_RANGE.max}" step="1">
          </label>
          <label class="fgset-row">
            <span>Mouse sensitivity <em data-fg="mouseSensVal"></em></span>
            <input type="range" data-fg="mouseSens" min="${MOUSE_SENSITIVITY_RANGE.min}" max="${MOUSE_SENSITIVITY_RANGE.max}" step="0.0002">
          </label>
          <label class="fgset-row">
            <span>Aim sensitivity <em data-fg="aimSensVal"></em></span>
            <input type="range" data-fg="aimSens" min="${AIM_SENSITIVITY_RANGE.min}" max="${AIM_SENSITIVITY_RANGE.max}" step="0.0002">
          </label>
          <label class="fgset-row">
            <span>Camera smoothing <em data-fg="smoothingVal"></em></span>
            <input type="range" data-fg="smoothing" min="${CAMERA_SMOOTHING_RANGE.min}" max="${CAMERA_SMOOTHING_RANGE.max}" step="0.01">
          </label>
          <label class="fgset-row">
            <span>Ball focus (tactical cam)</span>
            <select data-fg="ballFocus"></select>
          </label>
          <label class="fgset-row">
            <span>Invert look Y</span>
            <input type="checkbox" data-fg="invertY">
          </label>
        </section>

        <section>
          <h3>HUD</h3>
          <label class="fgset-row">
            <span>Aiming crosshair</span>
            <input type="checkbox" data-fg="crosshairEnabled">
          </label>
          <label class="fgset-row">
            <span>Crosshair size</span>
            <select data-fg="crosshairSize"></select>
          </label>
        </section>

        <section>
          <h3>GRAPHICS</h3>
          <label class="fgset-row">
            <span>Quality</span>
            <select data-fg="quality"></select>
          </label>
          <p class="fgset-note" data-fg="qualityNote"></p>
        </section>

        <section>
          <h3>MATCH</h3>
          <label class="fgset-row">
            <span>Half length</span>
            <select data-fg="halfMinutes"></select>
          </label>
          <label class="fgset-row">
            <span>AI difficulty (practice)</span>
            <select data-fg="difficulty"></select>
          </label>
        </section>

        <section>
          <h3>CONTROLS <span class="fgset-hint">click a key to rebind</span></h3>
          <!--
            Owner: "show a keyboard in the controls please and show arrows to
            explain". An illustrative diagram of the movement cluster, not a
            second control surface — the REAL current bindings (including
            whatever the player has rebound these four to) are the dynamic
            list below, .fgset-binds; this static block exists to answer
            "where do my fingers go" at a glance before reading forty rows of
            text. WASD labelled with the direction each key drives, laid out
            with the same row-stagger a real keyboard has (Q/W/E offset right
            of A/S/D, which sits right of the bottom row) so it reads as a
            keyboard and not a grid of buttons.
          -->
          <div class="fgset-kbd" aria-hidden="true">
            <div class="fgset-kbdrow">
              <span class="fgset-key">Q</span>
              <span class="fgset-key fgset-key-dir">W<i>▲</i></span>
              <span class="fgset-key">E</span>
            </div>
            <div class="fgset-kbdrow fgset-kbdrow-2">
              <span class="fgset-key fgset-key-dir">A<i>◀</i></span>
              <span class="fgset-key fgset-key-dir">S<i>▼</i></span>
              <span class="fgset-key fgset-key-dir">D<i>▶</i></span>
            </div>
            <div class="fgset-kbdrow fgset-kbdrow-3">
              <span class="fgset-key fgset-key-wide">Shift</span>
              <span class="fgset-key">Z</span>
              <span class="fgset-key">X</span>
              <span class="fgset-key">C</span>
            </div>
            <p class="fgset-hint">WASD moves — the highlighted keys and the arrow each drives</p>
          </div>
          <div class="fgset-mouse">
            <!-- Shoot/pass/call-for-pass used to be hardcoded here as static
                 "Left click"/"Right click"/"Middle click" text, which went
                 stale (and actively wrong) the moment a player rebound them —
                 they are ordinary REBINDABLE_ACTIONS entries now (mouse
                 buttons are just codes, see controlMap.ts) and already show
                 their REAL current binding in the list below. Only the two
                 rows with no dynamic entry elsewhere stay static. -->
            <span class="fgset-mouserow">Shot lift bias — <b>Mouse wheel</b></span>
            <span class="fgset-mouserow">Pause / menu — <b>Esc</b></span>
          </div>
          <div class="fgset-binds" data-fg="binds"></div>
          <p class="fgset-conflict" data-fg="conflictMsg"></p>
          <button class="fgset-reset" data-fg="resetBinds">RESET TO DEFAULTS</button>
        </section>

        <section>
          <h3>AUDIO</h3>
          <label class="fgset-row">
            <span>Master volume <em data-fg="volVal"></em></span>
            <input type="range" data-fg="volume" min="0" max="100" step="1">
          </label>
          <label class="fgset-row">
            <span>Music volume <em data-fg="musicVolVal"></em></span>
            <input type="range" data-fg="musicVolume" min="0" max="100" step="1">
          </label>
        </section>

      </div>
    </div>`;
  container.appendChild(root);

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  // `Element`, not `HTMLElement` — the camera preview below queries SVG nodes
  // (SVGGElement/SVGPolygonElement/SVGLineElement), which are Elements but
  // NOT HTMLElements, through this exact same lookup.
  const q = <T extends Element>(name: string): T =>
    root.querySelector(`[data-fg="${name}"]`) as T;

  const emit = (): void => opts.onChange(s);

  /*
   * THE CAMERA PREVIEW BOX. Reads `s.camera.{height,distance,fov}` (the same
   * live-edited state the sliders below already own) and redraws the SVG
   * markup added above. Pure geometry, no state of its own — called once at
   * init and again from `bindSlider`'s existing input handler, so it can
   * never drift out of sync with the numbers the sliders show.
   *
   * Height and distance are normalised to THEIR OWN range independently
   * (`norm`), not drawn to one shared metric scale — see the markup comment
   * above for why a shared scale would be meaningless at this box size.
   */
  const PLAYER_X = 230;
  const GROUND_Y = 128;
  // Distance slider: min -> CAM_X_NEAR (close behind the player marker),
  // max -> CAM_X_FAR (near the box's left edge).
  const CAM_X_NEAR = 210;
  const CAM_X_FAR = 40;
  // Height slider: min -> CAM_Y_LOW (just off the ground line), max ->
  // CAM_Y_HIGH (near the top of the box).
  const CAM_Y_LOW = 122;
  const CAM_Y_HIGH = 15;
  const norm = (v: number, range: { min: number; max: number }): number =>
    (v - range.min) / (range.max - range.min);

  const camPreviewCam = q<SVGGElement>('camPreviewCam');
  const camPreviewFov = q<SVGPolygonElement>('camPreviewFov');
  const camPreviewSight = q<SVGLineElement>('camPreviewSight');

  function updateCamPreview(): void {
    const camX = CAM_X_NEAR + (CAM_X_FAR - CAM_X_NEAR) * norm(s.camera.distance, CAMERA_DISTANCE_RANGE);
    const camY = CAM_Y_LOW + (CAM_Y_HIGH - CAM_Y_LOW) * norm(s.camera.height, CAMERA_HEIGHT_RANGE);
    camPreviewCam.setAttribute('transform', `translate(${camX}, ${camY})`);
    camPreviewSight.setAttribute('x1', String(camX));
    camPreviewSight.setAttribute('y1', String(camY));
    camPreviewSight.setAttribute('x2', String(PLAYER_X));
    camPreviewSight.setAttribute('y2', String(GROUND_Y));

    // The FOV wedge: two rays from the camera, split ± half the FOV around
    // the camera->player sightline (not the vertical or horizontal axis —
    // the wedge always points AT the player, wherever the camera marker
    // currently sits), extended past the player so the wedge visibly
    // encloses the marker rather than terminating exactly on it.
    const dx = PLAYER_X - camX;
    const dy = GROUND_Y - camY;
    const sightAngle = Math.atan2(dy, dx);
    const halfFovRad = (s.camera.fov / 2) * (Math.PI / 180);
    const rayLen = Math.hypot(dx, dy) * 1.35;
    const a1 = sightAngle - halfFovRad;
    const a2 = sightAngle + halfFovRad;
    const p1x = camX + Math.cos(a1) * rayLen;
    const p1y = camY + Math.sin(a1) * rayLen;
    const p2x = camX + Math.cos(a2) * rayLen;
    const p2y = camY + Math.sin(a2) * rayLen;
    camPreviewFov.setAttribute('points', `${camX},${camY} ${p1x},${p1y} ${p2x},${p2y}`);
  }

  // ── Camera ────────────────────────────────────────────────────────────────
  const camMode = q<HTMLSelectElement>('camMode');
  for (const m of MODES) {
    const o = document.createElement('option');
    o.value = String(m);
    o.textContent = cameraModeName(m);
    camMode.appendChild(o);
  }
  camMode.value = String(s.camera.mode);
  camMode.addEventListener('change', () => {
    s.camera.mode = Number(camMode.value);
    opts.live?.camera?.setMode(s.camera.mode);
    emit();
  });

  const bindSlider = (
    name: 'height' | 'distance' | 'fov',
    valName: string,
    unit: string,
  ): void => {
    const input = q<HTMLInputElement>(name);
    const val = q<HTMLElement>(valName);
    input.value = String(s.camera[name]);
    val.textContent = `${s.camera[name]}${unit}`;
    input.addEventListener('input', () => {
      s.camera[name] = Number(input.value);
      val.textContent = `${s.camera[name]}${unit}`;
      applyCameraSettings(s); // TUNABLES.camera — read live by render/camera.ts
      updateCamPreview();
      emit();
    });
  };
  bindSlider('height', 'heightVal', ' m');
  bindSlider('distance', 'distanceVal', ' m');
  bindSlider('fov', 'fovVal', '°');
  updateCamPreview();

  // ── Mouse & camera feel (§7.1) ──────────────────────────────────────────────
  // Sensitivities live on the InputManager instance (not TUNABLES), so they push
  // through the input live hook; smoothing rides TUNABLES via applyCameraSettings.
  const applyFeel = (): void =>
    opts.live?.input?.setSensitivity(s.camera.mouseSensitivity, s.camera.aimSensitivity);
  const bindSensSlider = (
    name: 'mouseSensitivity' | 'aimSensitivity',
    inputName: string,
    valName: string,
  ): void => {
    const el = q<HTMLInputElement>(inputName);
    const val = q<HTMLElement>(valName);
    el.value = String(s.camera[name]);
    val.textContent = s.camera[name].toFixed(4);
    el.addEventListener('input', () => {
      s.camera[name] = Number(el.value);
      val.textContent = s.camera[name].toFixed(4);
      applyFeel();
      emit();
    });
  };
  bindSensSlider('mouseSensitivity', 'mouseSens', 'mouseSensVal');
  bindSensSlider('aimSensitivity', 'aimSens', 'aimSensVal');

  const smoothingEl = q<HTMLInputElement>('smoothing');
  const smoothingVal = q<HTMLElement>('smoothingVal');
  smoothingEl.value = String(s.camera.smoothing);
  smoothingVal.textContent = `${s.camera.smoothing.toFixed(2)} s`;
  smoothingEl.addEventListener('input', () => {
    s.camera.smoothing = Number(smoothingEl.value);
    smoothingVal.textContent = `${s.camera.smoothing.toFixed(2)} s`;
    applyCameraSettings(s); // TUNABLES.camera.smoothTime — read live by render/camera.ts
    emit();
  });

  const ballFocusSel = q<HTMLSelectElement>('ballFocus');
  for (const v of BALL_FOCUS_CHOICES) {
    const o = document.createElement('option');
    o.value = String(v);
    o.textContent = BALL_FOCUS_LABELS[v] ?? String(v);
    ballFocusSel.appendChild(o);
  }
  ballFocusSel.value = String(s.camera.ballFocus);
  ballFocusSel.addEventListener('change', () => {
    s.camera.ballFocus = Number(ballFocusSel.value); // read live from settings by the frame loop
    emit();
  });

  const invertYEl = q<HTMLInputElement>('invertY');
  invertYEl.checked = s.camera.invertY;
  invertYEl.addEventListener('change', () => {
    s.camera.invertY = invertYEl.checked;
    emit();
  });

  // ── HUD (the Valorant-style centre reticle, live ask) ────────────────────
  // No `live` hook needed: the game loop (main.ts/practice.ts/testArena.ts)
  // reads `settings.crosshairEnabled`/`crosshairSize` straight into HudState
  // every single frame already, so an edit here reaches the HUD on the very
  // next frame for free — see settings.ts's field comment.
  const crosshairEnabledEl = q<HTMLInputElement>('crosshairEnabled');
  crosshairEnabledEl.checked = s.crosshairEnabled;
  crosshairEnabledEl.addEventListener('change', () => {
    s.crosshairEnabled = crosshairEnabledEl.checked;
    emit();
  });

  const crosshairSizeSel = q<HTMLSelectElement>('crosshairSize');
  for (const i of CROSSHAIR_SIZE_CHOICES) {
    const o = document.createElement('option');
    o.value = String(i);
    o.textContent = CROSSHAIR_SIZE_PRESETS[i]?.label ?? String(i);
    crosshairSizeSel.appendChild(o);
  }
  crosshairSizeSel.value = String(s.crosshairSize);
  crosshairSizeSel.addEventListener('change', () => {
    s.crosshairSize = Number(crosshairSizeSel.value);
    emit();
  });

  // ── Graphics ──────────────────────────────────────────────────────────────
  const quality = q<HTMLSelectElement>('quality');
  const qualityNote = q<HTMLElement>('qualityNote');
  for (const c of QUALITY_CHOICES) {
    const o = document.createElement('option');
    o.value = c;
    o.textContent = c === 'auto' ? 'Auto (detect)' : c[0]!.toUpperCase() + c.slice(1);
    quality.appendChild(o);
  }
  quality.value = s.quality;
  const refreshQualityNote = (): void => {
    qualityNote.textContent =
      s.quality === 'auto'
        ? 'Auto-detects from your GPU at match start.'
        : opts.live?.renderer
          ? 'Applied live.'
          : 'Applies when the next match starts.';
  };
  refreshQualityNote();
  quality.addEventListener('change', () => {
    s.quality = quality.value as QualityChoice;
    if (s.quality !== 'auto') opts.live?.renderer?.setQuality(s.quality);
    refreshQualityNote();
    emit();
  });

  // ── Match ─────────────────────────────────────────────────────────────────
  const halfSel = q<HTMLSelectElement>('halfMinutes');
  for (const m of HALF_MINUTES_CHOICES) {
    const o = document.createElement('option');
    o.value = String(m);
    o.textContent = `${m} min halves`;
    halfSel.appendChild(o);
  }
  halfSel.value = String(s.halfMinutes);
  halfSel.addEventListener('change', () => {
    s.halfMinutes = Number(halfSel.value);
    emit();
  });

  const diffSel = q<HTMLSelectElement>('difficulty');
  for (const d of DIFFICULTY_CHOICES) {
    const o = document.createElement('option');
    o.value = d;
    o.textContent = d[0]!.toUpperCase() + d.slice(1);
    diffSel.appendChild(o);
  }
  diffSel.value = s.difficulty;
  diffSel.addEventListener('change', () => {
    s.difficulty = diffSel.value as GameSettings['difficulty'];
    emit();
  });

  // ── Controls (rebind) ─────────────────────────────────────────────────────
  const bindsEl = q<HTMLElement>('binds');
  const conflictMsg = q<HTMLElement>('conflictMsg');
  let capture: { action: BindableAction; oldCode: string | null; chip: HTMLElement } | null = null;
  let conflictTimer = 0;

  const applyControls = (): void => {
    opts.live?.input?.setMap(s.controls);
    emit();
  };

  const cancelCapture = (): void => {
    if (!capture) return;
    capture.chip.classList.remove('fgset-capturing');
    capture.chip.textContent =
      capture.oldCode !== null ? keyLabel(capture.oldCode) : '+';
    capture = null;
  };

  const showConflict = (text: string, action: BindableAction): void => {
    conflictMsg.textContent = text;
    conflictMsg.style.opacity = '1';
    const row = bindsEl.querySelector(`[data-action="${action}"]`);
    if (row) {
      row.classList.add('fgset-flash');
      window.setTimeout(() => row.classList.remove('fgset-flash'), 900);
    }
    window.clearTimeout(conflictTimer);
    conflictTimer = window.setTimeout(() => {
      conflictMsg.style.opacity = '0';
    }, 2600);
  };

  /** Capture-phase listener so a rebind press NEVER reaches the game's own
   *  window-level key handlers (InputManager listens in the bubble phase). */
  const onCaptureKey = (e: KeyboardEvent): void => {
    if (!capture) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.code === 'Escape') {
      cancelCapture();
      return;
    }
    const { action, oldCode } = capture;
    const clash = conflictForKey(s.controls, e.code, action);
    if (clash !== null) {
      // Same key twice → highlight the owner, refuse the save (directive).
      cancelCapture();
      showConflict(
        `${keyLabel(e.code)} is already bound to "${ACTION_LABELS[clash]}" — unbind it there first.`,
        clash,
      );
      return;
    }
    let next = s.controls;
    if (oldCode !== null && oldCode !== e.code) next = unbindKey(next, oldCode);
    next = rebindKey(next, e.code, action);
    s.controls = next;
    capture = null;
    renderBinds();
    applyControls();
  };
  window.addEventListener('keydown', onCaptureKey, { capture: true });

  const renderBinds = (): void => {
    bindsEl.textContent = '';
    for (const action of REBINDABLE_ACTIONS) {
      const row = document.createElement('div');
      row.className = 'fgset-bindrow';
      row.dataset['action'] = action;
      const label = document.createElement('span');
      label.textContent = ACTION_LABELS[action];
      row.appendChild(label);

      const chips = document.createElement('span');
      chips.className = 'fgset-chips';
      const codes = keysForAction(s.controls, action);
      for (const code of codes) {
        const chip = document.createElement('button');
        chip.className = 'fgset-chip';
        chip.textContent = keyLabel(code);
        chip.title = 'Click, then press a new key (Esc cancels). Right-click to unbind.';
        chip.addEventListener('click', () => {
          cancelCapture();
          capture = { action, oldCode: code, chip };
          chip.classList.add('fgset-capturing');
          chip.textContent = 'press a key…';
        });
        chip.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          // Never let an action end up with zero keys from the UI — a dead
          // bind is a support ticket. The last key can only be MOVED.
          if (keysForAction(s.controls, action).length <= 1) {
            showConflict(`"${ACTION_LABELS[action]}" needs at least one key — rebind it instead.`, action);
            return;
          }
          s.controls = unbindKey(s.controls, code);
          renderBinds();
          applyControls();
        });
        chips.appendChild(chip);
      }
      if (codes.length === 0) {
        const chip = document.createElement('button');
        chip.className = 'fgset-chip fgset-chip-unbound';
        chip.textContent = 'unbound';
        chip.addEventListener('click', () => {
          cancelCapture();
          capture = { action, oldCode: null, chip };
          chip.classList.add('fgset-capturing');
          chip.textContent = 'press a key…';
        });
        chips.appendChild(chip);
      }
      row.appendChild(chips);
      bindsEl.appendChild(row);
    }
  };
  renderBinds();

  q<HTMLButtonElement>('resetBinds').addEventListener('click', () => {
    cancelCapture();
    s.controls = defaultControlMap();
    renderBinds();
    applyControls();
  });

  // ── Audio (§11 — live in a running match via the audio hook) ─────────────
  const vol = q<HTMLInputElement>('volume');
  const volVal = q<HTMLElement>('volVal');
  vol.value = String(Math.round(s.masterVolume * 100));
  volVal.textContent = `${Math.round(s.masterVolume * 100)}%`;
  vol.addEventListener('input', () => {
    s.masterVolume = Number(vol.value) / 100;
    volVal.textContent = `${vol.value}%`;
    opts.live?.audio?.setMasterVolume(s.masterVolume);
    opts.live?.music?.setVolume(s.musicVolume, s.masterVolume);
    emit();
  });

  const musicVol = q<HTMLInputElement>('musicVolume');
  const musicVolVal = q<HTMLElement>('musicVolVal');
  musicVol.value = String(Math.round(s.musicVolume * 100));
  musicVolVal.textContent = `${Math.round(s.musicVolume * 100)}%`;
  musicVol.addEventListener('input', () => {
    s.musicVolume = Number(musicVol.value) / 100;
    musicVolVal.textContent = `${musicVol.value}%`;
    opts.live?.music?.setVolume(s.musicVolume, s.masterVolume);
    emit();
  });

  // ── Close ─────────────────────────────────────────────────────────────────
  q<HTMLButtonElement>('close').addEventListener('click', () => {
    cancelCapture();
    opts.onClose();
  });

  return {
    dispose(): void {
      cancelCapture();
      window.removeEventListener('keydown', onCaptureKey, { capture: true });
      window.clearTimeout(conflictTimer);
      root.remove();
      style.remove();
    },
  };
}

/**
 * Settings overlay styling.
 *
 * Reads the same tokens as the rest of the menu (footy-arena.css) — it defines
 * no colours of its own. It used to hard-code the old #ffd94a yellow, native
 * OS selects and OS-coloured range inputs, so opening Settings from a gold-and-
 * navy menu dropped you into a different product.
 */
const CSS = `
.fgset {
  /* Owner: "theme same please" — this screen's own accent tokens
     (footy-arena.css's --fa-accent*) are still the OLD gold palette from
     before the FIFA-menu redesign moved everything else to green
     (arena-menu.css's --am-accent, #4ade80). Every rule below already reads
     its colour through var(--fa-accent...) rather than a literal, so
     redefining the five tokens HERE — a custom property set on this
     container cascades to every descendant that references it — re-themes
     sliders, checkboxes, the rebind chips and the panel's top edge all at
     once, with no other rule in this file touched. */
  --fa-accent: #4ade80;
  --fa-accent-hi: #79e8a6;
  --fa-accent-lo: #2fb862;
  --fa-accent-line: rgba(74, 222, 128, 0.5);
  --fa-accent-soft: rgba(74, 222, 128, 0.14);
  position: fixed; inset: 0;
  /* Written when 40 was the highest z-index in the menu layer (footy-arena.css's
     scale, which this file's own header says it reads from). The FIFA-menu
     redesign since put .am-root at 60 — Settings still opened (mounted,
     had content, no error) but rendered UNDER the menu it was supposed to sit
     over, which looked exactly like "clicking Settings does nothing" from
     outside. 70 clears every z-index anywhere in the UI layer (highest other
     use is .am-root/.fgdoors at 60) with headroom to spare. */
  z-index: 70;
  display: grid; place-items: center;
  padding: var(--space-6);
  /* A blurred scrim rather than a flat wash: the menu behind stays legible as
     context but stops competing for attention. The owner's tactics-room art
     sits under that same wash — .fgset-panel below is itself 98%+ opaque,
     so the image only ever shows in the margin around the panel, never
     behind its own text. background-color (not the background shorthand)
     specifically, so it layers with background-image instead of resetting
     it — the two used to be the same declaration before this had an image. */
  background-color: rgba(4, 7, 12, 0.7);
  background-image: url('/ui/backgrounds/settings.webp');
  background-size: cover;
  background-position: center right;
  backdrop-filter: blur(14px) saturate(0.9);
  font-family: var(--fa-ui);
  color: var(--fa-text);
  animation: fa-scrim-in 220ms var(--fa-ease) both;
}
@keyframes fa-scrim-in { from { opacity: 0; } to { opacity: 1; } }

.fgset-panel {
  width: min(760px, 100%);
  max-height: min(86vh, 780px);
  display: flex; flex-direction: column;
  background: linear-gradient(168deg, rgba(20, 32, 48, 0.98) 0%, rgba(8, 13, 21, 0.99) 100%);
  border: 1px solid var(--fa-hairline-hi);
  border-radius: var(--fa-r-lg);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7);
  overflow: hidden;
  animation: fa-panel-in 320ms var(--fa-ease) both;
}
/* Gold edge on top, the same marker the hero eyebrow and choice panels use. */
.fgset-panel::before {
  content: ''; display: block; height: 3px; flex: none;
  background: linear-gradient(90deg, var(--fa-accent), rgba(232, 197, 106, 0));
}
@keyframes fa-panel-in {
  from { opacity: 0; transform: translate3d(0, 14px, 0) scale(0.99); }
  to   { opacity: 1; transform: none; }
}

.fgset-head {
  display: flex; align-items: center; justify-content: space-between;
  flex: none;
  padding: var(--space-5) var(--space-6);
  border-bottom: 1px solid var(--fa-hairline);
}
.fgset-head h2 {
  margin: 0;
  font-family: var(--fa-display);
  font-size: 24px; font-weight: 700; letter-spacing: 0.14em;
}
.fgset-close {
  height: 34px; padding-inline: var(--space-4);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--fa-hairline-hi);
  border-radius: var(--fa-r-md);
  color: var(--fa-steel);
  font-family: var(--fa-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
  cursor: pointer;
  transition: background var(--fa-t-fast) var(--fa-ease), color var(--fa-t-fast) var(--fa-ease);
}
.fgset-close:hover { background: rgba(255, 255, 255, 0.09); color: var(--fa-text); }
.fgset-close:focus-visible { outline: none; border-color: var(--fa-accent); box-shadow: 0 0 0 2px var(--fa-accent-soft); }

.fgset-body { overflow-y: auto; padding: var(--space-2) var(--space-6) var(--space-6); scrollbar-gutter: stable; }
.fgset-body::-webkit-scrollbar { width: 10px; }
.fgset-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12); border-radius: 99px;
  border: 3px solid transparent; background-clip: content-box;
}
.fgset-body section { margin-top: var(--space-6); animation: fa-read 380ms var(--fa-ease-out) both; }
.fgset-body section:nth-of-type(2) { animation-delay: 50ms; }
.fgset-body section:nth-of-type(3) { animation-delay: 100ms; }
.fgset-body section:nth-of-type(4) { animation-delay: 150ms; }
.fgset-body h3 {
  position: relative;
  margin: 0 0 var(--space-4);
  padding-left: var(--space-4);
  font-family: var(--fa-display);
  font-size: 15px; font-weight: 700; letter-spacing: 0.14em;
  color: var(--fa-text);
}
.fgset-body h3::before {
  content: ''; position: absolute; left: 0; top: 50%; transform: translateY(-50%);
  width: 3px; height: 13px; border-radius: 2px; background: var(--fa-accent);
}
.fgset-hint {
  color: var(--fa-steel-dim); font-family: var(--fa-ui);
  font-weight: 500; letter-spacing: 0.02em; font-size: 11.5px; margin-left: var(--space-3);
}

/* ── Camera preview box (updateCamPreview in the script above draws into
   this SVG's markup on every height/distance/fov change) ────────────────── */
.fgset-campreview {
  margin: var(--space-3) 0 var(--space-4);
  padding: var(--space-3);
  border-radius: var(--fa-r-md);
  border: 1px solid var(--fa-hairline);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(0, 0, 0, 0.12));
}
/* width:100% with height:auto correctly holds the SVG's 280:150 aspect ratio
   — but 100% of the PANEL (up to 760px) stretched it to ~676x362, dwarfing
   the compact slider rows it sits above. A capped width keeps it a small
   supporting readout rather than the largest thing in the section. */
.fgset-campreview svg { display: block; width: 100%; max-width: 340px; height: auto; margin: 0 auto; }
.fgset-campreview .fgset-hint { margin: var(--space-2) 0 0; display: block; text-align: center; }
.fgcp-ground { stroke: rgba(255, 255, 255, 0.16); stroke-width: 1.5; }
.fgcp-player { fill: var(--fa-text); }
.fgcp-playerpin { stroke: rgba(255, 255, 255, 0.28); stroke-width: 1.5; stroke-dasharray: 2 2; }
/* The FOV wedge and its sightline share the accent colour (now green — see
   the .fgset accent-token override above) so the preview reads as ONE
   readout, the same colour language the sliders themselves already use. */
.fgcp-fov { fill: var(--fa-accent); fill-opacity: 0.16; stroke: var(--fa-accent); stroke-opacity: 0.4; stroke-width: 1; }
.fgcp-sight { stroke: var(--fa-accent); stroke-opacity: 0.55; stroke-width: 1; stroke-dasharray: 3 3; }
.fgcp-cam circle { fill: #0d1420; stroke: var(--fa-accent); stroke-width: 1.5; }
.fgcp-cam .fgcp-camlens { fill: var(--fa-accent); stroke: none; }

/* ── Keyboard diagram (illustrative — the real, rebindable-aware bindings are
   the dynamic .fgset-binds list further down) ───────────────────────────── */
.fgset-kbd {
  margin: var(--space-3) 0 var(--space-4);
  padding: var(--space-4) var(--space-3);
  border-radius: var(--fa-r-md);
  border: 1px solid var(--fa-hairline);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(0, 0, 0, 0.12));
}
.fgset-kbdrow { display: flex; justify-content: center; gap: 6px; }
.fgset-kbdrow + .fgset-kbdrow { margin-top: 6px; }
/* The real-keyboard stagger: each row nudges right of the one below it. */
.fgset-kbdrow-2 { margin-left: 18px; }
.fgset-kbdrow-3 { margin-left: 6px; }
.fgset-key {
  display: grid; place-items: center;
  min-width: 34px; height: 34px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid var(--fa-hairline-hi);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(0, 0, 0, 0.18));
  color: var(--fa-steel);
  font-family: var(--fa-display); font-weight: 700; font-size: 13px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), inset 0 -1px 0 rgba(0, 0, 0, 0.3);
}
.fgset-key-wide { min-width: 58px; font-size: 11px; }
/* The four movement keys carry the accent colour and their own arrow glyph,
   same "wedge = direction" colour language the camera preview uses — one
   look tells you both WHICH keys move you and WHICH WAY each one goes. */
.fgset-key-dir {
  position: relative;
  border-color: var(--fa-accent);
  color: var(--fa-accent);
  background: linear-gradient(180deg, rgba(74, 222, 128, 0.22), rgba(74, 222, 128, 0.06));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 0 10px rgba(74, 222, 128, 0.25);
}
.fgset-key-dir i {
  position: absolute; top: -17px; left: 50%; transform: translateX(-50%);
  font-style: normal; font-size: 11px; line-height: 1; color: var(--fa-accent);
}
.fgset-kbdrow .fgset-key-dir:nth-child(1) i { top: 50%; left: -17px; transform: translateY(-50%); }
.fgset-kbdrow .fgset-key-dir:nth-child(3) i { top: 50%; left: auto; right: -17px; transform: translateY(-50%); }
/* S sits between A and D in row 2 — its arrow floats BELOW rather than the
   default above, so all four arrows point outward from the WASD cluster in
   the same direction their glyph does, instead of S's down-arrow floating
   above its key like W's up-arrow does. */
.fgset-kbdrow-2 .fgset-key-dir:nth-child(2) i { top: auto; bottom: -17px; left: 50%; transform: translateX(-50%); }
.fgset-kbd .fgset-hint { margin: var(--space-3) 0 0; display: block; text-align: center; }

.fgset-row {
  display: grid;
  grid-template-columns: minmax(0, 230px) 1fr;
  align-items: center; gap: var(--space-5);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--fa-hairline);
  font-size: 13.5px;
}
.fgset-row > span { color: var(--fa-steel); }
.fgset-row em {
  font-style: normal; color: var(--fa-accent); margin-left: var(--space-2);
  font-family: var(--fa-display); font-weight: 700; font-variant-numeric: tabular-nums;
}

/* Sliders: a thin steel track with a gold thumb, replacing accent-color so the
   control matches the rest of the system in both Chrome and Firefox. */
.fgset-row input[type=range] {
  width: 100%; height: 22px; background: none; cursor: pointer; -webkit-appearance: none; appearance: none;
}
.fgset-row input[type=range]::-webkit-slider-runnable-track {
  height: 4px; border-radius: 99px; background: rgba(255, 255, 255, 0.14);
}
.fgset-row input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  width: 16px; height: 16px; margin-top: -6px; border-radius: 50%; border: 0;
  background: linear-gradient(180deg, var(--fa-accent-hi), var(--fa-accent-lo));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  transition: transform var(--fa-t-fast) var(--fa-ease);
}
.fgset-row input[type=range]:hover::-webkit-slider-thumb { transform: scale(1.18); }
.fgset-row input[type=range]::-moz-range-track { height: 4px; border-radius: 99px; background: rgba(255,255,255,0.14); }
.fgset-row input[type=range]::-moz-range-thumb {
  width: 16px; height: 16px; border: 0; border-radius: 50%; background: var(--fa-accent);
}
.fgset-row input[type=range]:focus-visible { outline: none; }
.fgset-row input[type=range]:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px var(--fa-accent-soft); }

/* Native selects, restyled — a light chevron drawn in the background so there
   is no OS-coloured arrow sitting in a dark panel. */
.fgset-row select {
  width: 100%; height: 38px; padding: 0 34px 0 var(--space-4);
  background-color: rgba(6, 10, 17, 0.7);
  background-image: linear-gradient(45deg, transparent 50%, var(--fa-steel) 50%),
                    linear-gradient(135deg, var(--fa-steel) 50%, transparent 50%);
  background-position: calc(100% - 18px) 16px, calc(100% - 13px) 16px;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  color: var(--fa-text);
  border: 1px solid var(--fa-hairline-hi); border-radius: var(--fa-r-md);
  font-family: var(--fa-ui); font-size: 13.5px;
  -webkit-appearance: none; appearance: none; cursor: pointer;
}
.fgset-row select:focus-visible { outline: none; border-color: var(--fa-accent); box-shadow: 0 0 0 2px var(--fa-accent-soft); }
.fgset-row input[type=checkbox] {
  width: 18px; height: 18px; accent-color: var(--fa-accent); cursor: pointer; justify-self: start;
}

.fgset-note { margin: var(--space-2) 0 0; font-size: 11.5px; color: var(--fa-steel-dim); }
.fgset-mouse { display: flex; flex-direction: column; gap: 2px; margin-bottom: var(--space-3); }
.fgset-mouserow { font-size: 12px; color: var(--fa-steel-dim); }
.fgset-mouserow b { color: var(--fa-text); }

.fgset-binds { display: flex; flex-direction: column; gap: 2px; }
.fgset-bindrow {
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
  padding: var(--space-2) var(--space-3); font-size: 13px;
  border-radius: var(--fa-r-sm);
  color: var(--fa-steel);
}
.fgset-bindrow:nth-child(odd) { background: rgba(255, 255, 255, 0.03); }
.fgset-bindrow.fgset-flash { background: rgba(224, 101, 95, 0.3); transition: background 150ms; }
.fgset-chips { display: flex; gap: var(--space-2); flex-wrap: wrap; justify-content: flex-end; }
.fgset-chip {
  min-width: 36px; padding: 4px var(--space-3);
  background: rgba(6, 10, 17, 0.7);
  color: var(--fa-text);
  border: 1px solid var(--fa-hairline-hi);
  border-radius: var(--fa-r-sm);
  font-family: var(--fa-ui); font-size: 12px; font-weight: 700;
  cursor: pointer;
  transition: border-color var(--fa-t-fast) var(--fa-ease), background var(--fa-t-fast) var(--fa-ease);
}
.fgset-chip:hover { border-color: var(--fa-accent-line); background: rgba(255, 255, 255, 0.06); }
.fgset-chip-unbound { opacity: 0.45; font-weight: 500; }
.fgset-capturing {
  border-color: var(--fa-accent); color: var(--fa-accent); background: var(--fa-accent-soft);
  animation: fgsetPulse 0.9s infinite;
}
@keyframes fgsetPulse { 50% { opacity: 0.5; } }
.fgset-conflict { min-height: 16px; margin: var(--space-2) 0 0; font-size: 12px; color: #ff8f8f; opacity: 0; transition: opacity 200ms; }
.fgset-reset {
  margin-top: var(--space-3);
  height: 36px; padding-inline: var(--space-5);
  background: rgba(224, 101, 95, 0.08);
  border: 1px solid rgba(224, 101, 95, 0.5);
  border-radius: var(--fa-r-md);
  color: #e0655f;
  font-family: var(--fa-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.06em;
  cursor: pointer;
  transition: background var(--fa-t-fast) var(--fa-ease), color var(--fa-t-fast) var(--fa-ease);
}
.fgset-reset:hover { background: rgba(224, 101, 95, 0.18); color: #ff8f8f; }
`;
